import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../../models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { Mp1SensorIds, Mp1SensorTitles, Mp2SensorIds, Mp2SensorTitles, TritonSensors, getSensorFieldId } from '../base-config-models';
import { SmartDepthSensorFieldIds } from './config-models';
import { MonitoringPointElement } from 'app/shared/components/location-card/models/location-card.model';
import { LocationCardSettings } from '../../../reducers';
import { field } from '../../../core/field-schema';
import { required, pattern } from '../../../core/validator-builders';
import { SmartDepthDevice } from '../models';

/**
 * Smart Depth sensor configuration
 */
export const smartDepthSensor = (mp: 0 | 1, settings: LocationCardSettings, sensorData: SmartDepthDevice): Section => {
    const isMetric = settings.isMetric;

    return {
        info: {
            id: mp === 0 ? Mp1SensorIds[TritonSensors.SmartDepth] : Mp2SensorIds[TritonSensors.SmartDepth],
            title: mp === 0 ? Mp1SensorTitles[TritonSensors.SmartDepth] : Mp2SensorTitles[TritonSensors.SmartDepth],
            expandable: true,
            displayTitle: true,
            expanded: false,
        },
        rows: [
            // Physical Offset Row
            {
                info: {
                    id: 'physical-offset-row',
                    panelClass: 'custom-50p form-input-bottom-space',
                },
                items: [
                    field.number(getSensorFieldId(SmartDepthSensorFieldIds.PhysicalOffset, mp), 'physicalOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_PHYSICAL',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.physicalOffset : 0,
                        validators: [
                            required(),
                            pattern({
                                value: isMetric
                                    ? REGEX_CONFIG.negativeWithOneDecimalPattern
                                    : REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'full-width required',
                    }),
                ],
            },

            // Electronic Offset, Mode, Blanking Row
            {
                info: {
                    id: 'electronic-mode-blanking-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.number(getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicElectronicOffset, mp), 'ultrasonicElectronicOffset', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ELECTRONIC',
                        placeholderSuffix: settings.depthUnit,
                        value: sensorData ? sensorData.ultrasonicElectronicOffset : 0,
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.negativeWithTwoDecimalPattern,
                                message: isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                        panelClass: 'each-Item width-30',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicMode, mp), 'ultrasonicMode', {
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MODE',
                        value: sensorData ? sensorData.ultrasonicMode : 0,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.ModeList,
                        panelClass: 'each-Item width-30 required',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicBlanking, mp), 'ultrasonicBlanking', {
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_BLANKING',
                        value: sensorData ? sensorData.ultrasonicBlanking : 0,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.BlankingList,
                        panelClass: 'each-Item width-30 required',
                    }),
                ],
            },

            // Store Data & Surcharge Row
            {
                info: {
                    id: 'store-data-surcharge-row',
                    panelClass: 'custom-50p pad-b-15',
                },
                items: [
                    field.toggle(getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicStoreData, mp), 'ultrasonicStoreData', {
                        value: sensorData ? sensorData.ultrasonicStoreData : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_STORE_DATA',
                        panelClass: 'each-Item custom-slide-toggle item-flex-center',
                    }),
                    field.toggle(getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicDisableSurchargeDetection, mp), 'ultrasonicDisableSurchargeDetection', {
                        value: sensorData ? sensorData.ultrasonicDisableSurchargeDetection : true,
                        label: 'ADD_EDIT.INACTIVE_TOGGLE_SURCHARGE',
                        panelClass: 'each-Item custom-slide-toggle item-flex-center',
                    }),
                ],
            },

            // Info Text Row
            {
                info: {
                    id: 'info-text-row',
                    panelClass: 'input-text mt-15',
                },
                items: [
                    {
                        id: 'info-text',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'infoText',
                        label: 'ADD_EDIT.ADD_EDIT_TEXT',
                        panelClass: 'toggle toggle-text input-text font-600',
                    },
                ],
            },

            // Temperature Store & Sensor Row
            {
                info: {
                    id: 'temperature-store-sensor-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(getSensorFieldId(SmartDepthSensorFieldIds.TemperatureStoreData, mp), 'temperatureStoreData', {
                        value: sensorData ? sensorData.temperatureStoreData : true,
                        label: 'ADD_EDIT.ACTIVE_TOGGLE_STORE',
                        panelClass: 'each-Item custom-slide-toggle item-flex-center',
                    }),
                    field.select<MonitoringPointElement, number>(getSensorFieldId(SmartDepthSensorFieldIds.TemperatureSensor, mp), 'temperatureSensor', {
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSOR',
                        value: sensorData ? sensorData.temperatureSensor : 1,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.TemperatureSensors,
                        panelClass: 'each-Item full-width required',
                    }),
                ],
            },

            // Hidden passthrough field (populated from API, not displayed)
            {
                info: {
                    id: 'smart-depth-hidden-fields-row',
                },
                items: [
                    field.number(getSensorFieldId(SmartDepthSensorFieldIds.UltrasonicDacGain, mp), 'ultrasonicDacGain', {
                        value: sensorData ? sensorData.ultrasonicDacGain : 0,
                        visible: false,
                    }),
                ],
            },
        ],
    };
};
